import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Panel from 'src/components/common/Panel';

export interface ILm1099SearchCriteria {
    lmNo?: string;
    taxYear?: string;
}

interface ILm1099SearchProps {
    onSearch: (criteria: ILm1099SearchCriteria) => void;
}

interface IOptionType {
    value: string;
    label: string;
}

interface ISearchableLandman {
    lmNo: number;
    tsdNo: number;
    firstName: string;
    lastName: string;
}

// Landman No. lookahead (debounced fetch against the existing Landmen search-by-category endpoint,
// which already queries the real LM table directly - the same table Landman No./LM_1099 key off of)
// plus a Tax Year filter - the two fields that key the LM_1099 grid, mirroring Ap1099Search.tsx.
//
// Despite the column name, LM_1099.LM_NO's values are actually LM.TSD_NO (confirmed via
// usp_SeedLm1099's own ACCT_NO-to-TSD_NO convention, and every 1099 report proc joins on TSD_NO) -
// LM.LM_NO is a different value entirely (it's the den_out encryption key, not the "landman number"
// shown throughout this feature). Options here must carry tsdNo, not lmNo, or every search comes
// back empty.
const Lm1099Search = (props: ILm1099SearchProps) => {
    const { control, getValues, reset, register } = useForm<ILm1099SearchCriteria>();
    const { get, response } = useFetch();
    const [landmanOptions, setLandmanOptions] = useState<Array<IOptionType>>([]);
    const [isSearchingLandmen, setIsSearchingLandmen] = useState<boolean>(false);

    useEffect(() => {
        fetchLandmen('');
    }, []);

    const fetchLandmen = async (term: string) => {
        const result: Array<ISearchableLandman> = await get(`/v1.0/landmen/personnel/search-by-category?term=${encodeURIComponent(term)}`);

        if (response.ok && Array.isArray(result)) {
            setLandmanOptions(result.map((lm) => ({
                value: `${lm.tsdNo}`,
                label: `${lm.firstName} ${lm.lastName} (${lm.tsdNo})`
            })));
        }
    };

    const landmanSearch = _.debounce((inputText: string) => {
        fetchLandmen(inputText);
    }, 500);

    const handleSearch = () => {
        const data = getValues();
        props.onSearch({ lmNo: data.lmNo, taxYear: data.taxYear });
    };

    const handleClear = () => {
        reset();
        setLandmanOptions([]);
        props.onSearch({});
    };

    return (
        <Panel backgroundColor="#D3D3D3">
            <Panel panelTitle="Search">
                <div style={{ padding: '0 .5rem', marginTop: '-1rem' }}>
                    <div className="form-row">
                        <div style={{ width: '45%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Landman</label>
                            <Controller
                                name="lmNo"
                                control={control}
                                defaultValue={null}
                                render={({ onChange, value }) => (
                                    <Select
                                        placeholder="Select Landman"
                                        options={landmanOptions}
                                        value={value ? (landmanOptions.find((o) => o.value === value) ?? null) : null}
                                        onMenuOpen={() => setIsSearchingLandmen(true)}
                                        onMenuClose={() => setIsSearchingLandmen(false)}
                                        onBlur={() => setIsSearchingLandmen(false)}
                                        menuIsOpen={isSearchingLandmen}
                                        isSearchable={true}
                                        isClearable={true}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        onInputChange={(text, { action }) => {
                                            if (action === 'input-change') {
                                                landmanSearch(text);
                                            }
                                        }}
                                        onChange={(opt: IOptionType | null) => onChange(opt?.value ?? null)}
                                    />
                                )}
                            />
                        </div>
                        <div style={{ width: '20%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Tax Year</label>
                            <input
                                type="number"
                                name="taxYear"
                                ref={register}
                                style={{ width: '100%', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>
                        <div style={{ display: 'flex', marginLeft: 'auto', alignItems: 'center' }}>
                            <Button
                                text={'Search'}
                                className="blue-hover-button"
                                style={{ width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                                onClick={() => handleSearch()}
                            />
                            <Button
                                text="Clear"
                                className="blue-hover-button"
                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                onClick={() => handleClear()}
                            />
                        </div>
                    </div>
                </div>
            </Panel>
        </Panel>
    );
};

export default Lm1099Search;



// WEBPACK FOOTER //
// ./src/components/personnel/components/Lm1099Search.tsx